import { isApiMode } from './baseUrl';
import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// DEV PERSONAS — «switch to global admin / centre admin / club admin /
// player» without a password. DEVELOPMENT ONLY.
//
// THE CLIENT HAS NO FLAG OF ITS OWN. It asks the API whether personas exist;
// in every deployment that has not opted in the route does not exist and
// answers 404, so nothing is rendered. One control, on the server, rather
// than a build flag that could drift from it (routes/devPersonas.ts).
//
// A PERSONA IS A CONTEXT SWITCH, NOT A WAY IN (#444). These calls carry the
// caller's CURRENT staff session. `/sa` has its own local dev login and real
// staff RBAC; a persona is what that signed-in user switches INTO, to drive
// the club and member surfaces without an Entra tenant or a join code.
//
// The router used to be how a session was obtained from NOTHING, and giving
// it a secret of its own would have been a second authentication system for
// the same door. `apiFetch` sends the session the way every other call does,
// and the API refuses a member token — so a persona cannot bootstrap another
// persona.
// ─────────────────────────────────────────────────────────────────────

export interface DevPersona {
  // The server owns this list. `ga` was removed in #444 — no persona
  // carries platform authority — and the union is kept open because the
  // bar renders whatever the deployment offers.
  key: 'centre' | 'club' | 'player' | string;
  label: string;
  kind: 'club' | 'member';
  /**
   * The persona's own account address — how the DEV menu recognises which
   * persona the signed-in account IS after a reload or an ordinary sign-in,
   * not only right after a switch. Null for the member persona, which has no
   * account; absent from an API older than the field.
   */
  email?: string | null;
}

/**
 * Which persona the signed-in account is, or null.
 *
 * The persona the page switched to in this life wins; otherwise the account's
 * address is matched against the personas' own. Case-insensitive, because the
 * server compares addresses that way too.
 */
export function activePersonaKey(
  personas: readonly DevPersona[],
  switchedTo: string | null,
  accountEmail: string | null | undefined,
): string | null {
  if (switchedTo) return switchedTo;
  const email = (accountEmail ?? '').trim().toLowerCase();
  if (!email) return null;
  return personas.find((p) => (p.email ?? '').toLowerCase() === email)?.key ?? null;
}

/** The personas the API offers, or [] when the feature does not exist here. */
export async function fetchDevPersonas(
  fetchImpl: typeof apiFetch = apiFetch,
): Promise<DevPersona[]> {
  if (!isApiMode()) return [];
  try {
    const res = await fetchImpl('/auth/dev/personas');
    if (!res.ok) return [];
    const body = (await res.json().catch(() => null)) as { personas?: unknown } | null;
    return Array.isArray(body?.personas) ? (body.personas as DevPersona[]) : [];
  } catch {
    return [];
  }
}

export type DevPersonaSession =
  | {
      kind: 'club';
      token: string;
      expiresAt: string;
      account: { id: string; displayName: string | null; email: string | null };
    }
  | {
      kind: 'member';
      token: string;
      expiresAt: string;
      assurance: string;
      member: { id: string; name: string | null };
      club: { id: string | null; slug: string; name: string | null };
    };

export type DevPersonaResult =
  | { state: 'ok'; session: DevPersonaSession }
  | { state: 'refused'; message: string }
  | { state: 'error' };

/** Obtain a REAL session for a persona. */
export async function startDevPersona(
  key: string,
  fetchImpl: typeof apiFetch = apiFetch,
): Promise<DevPersonaResult> {
  if (!isApiMode()) return { state: 'error' };
  try {
    const res = await fetchImpl(`/auth/dev/personas/${encodeURIComponent(key)}/session`, {
      method: 'POST',
    });
    if (res.status === 404 || res.status === 409) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      return { state: 'refused', message: body?.message ?? 'Personaen finnes ikke her.' };
    }
    if (!res.ok) return { state: 'error' };
    const body = (await res.json().catch(() => null)) as DevPersonaSession | null;
    if (!body || typeof body.token !== 'string' || !body.token) return { state: 'error' };
    return { state: 'ok', session: body };
  } catch {
    return { state: 'error' };
  }
}

// ── v2: THE RBAC SCENARIO BUILDER (#1024) ───────────────────────────
//
// Any organizations, any of their roles, applied to an ISOLATED dev identity
// (`dev-rbac-N@starpoint.dev`) and signed in as it. The server owns every
// rule — which roles belong to which organization, that only dev identities
// are written, that nothing is a platform role — and the browser renders what
// it is told. The effective capability UNION is read from `/me` afterwards,
// never computed here.

export interface ScenarioRole {
  id: string;
  key: string;
  /** The role's name as the database holds it — data, not catalog text. */
  name: string;
  isSystem: boolean;
  /** What the role holds, so the tool can say which role contributes a right. */
  capabilities: string[];
}

export interface ScenarioOrganization {
  id: string;
  slug: string;
  name: string;
  kind: string;
  /** The roles this organization may be granted: system ones and its own. */
  roles: ScenarioRole[];
}

export interface ScenarioGrant {
  organizationId: string;
  roleIds: string[];
}

export interface ScenarioIdentity {
  slot: number;
  email: string;
  displayName: string;
  provisioned: boolean;
  /** What the identity holds NOW — the builder opens on it. */
  grants: ScenarioGrant[];
  player: boolean;
}

export interface ScenarioCatalog {
  identities: ScenarioIdentity[];
  organizations: ScenarioOrganization[];
}

/** The catalog, or null where the feature does not exist (or not for this account). */
export async function fetchScenarioCatalog(
  fetchImpl: typeof apiFetch = apiFetch,
): Promise<ScenarioCatalog | null> {
  if (!isApiMode()) return null;
  try {
    const res = await fetchImpl('/auth/dev/personas/scenarios');
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as ScenarioCatalog | null;
    if (!body || !Array.isArray(body.organizations) || !Array.isArray(body.identities)) {
      return null;
    }
    return body;
  } catch {
    return null;
  }
}

export interface ScenarioRequest {
  slot: number;
  grants: ScenarioGrant[];
  player: boolean;
}

export type ScenarioResult =
  | { state: 'ok'; session: Extract<DevPersonaSession, { kind: 'club' }> }
  | { state: 'refused'; code: string }
  | { state: 'error' };

/**
 * Apply a scenario and obtain a REAL club session for its identity.
 *
 * A refusal carries the server's CODE (`role_not_in_organization`,
 * `scenario_changed`, …) — the sentence is the caller's to choose (#310a).
 */
export async function startScenario(
  scenario: ScenarioRequest,
  fetchImpl: typeof apiFetch = apiFetch,
): Promise<ScenarioResult> {
  if (!isApiMode()) return { state: 'error' };
  try {
    const res = await fetchImpl('/auth/dev/personas/scenarios/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scenario),
    });
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      return { state: 'refused', code: typeof body?.error === 'string' ? body.error : 'refused' };
    }
    if (!res.ok || !body || typeof body.token !== 'string' || !body.token) {
      return { state: 'error' };
    }
    return {
      state: 'ok',
      session: body as unknown as Extract<DevPersonaSession, { kind: 'club' }>,
    };
  } catch {
    return { state: 'error' };
  }
}
